/** Resolve a comparable timestamp (ms) for sorting payment history newest-first. */
export const getDirectPaymentTimestamp = (
    payment,
    eventTimes = {},
    requestAcceptedEventTimes = {},
) => {
    const requestId = Number(payment?.requestId);
    const isRequestRow =
        Number.isFinite(requestId) ||
        String(payment?.type || '').toLowerCase().includes('request');

    if (isRequestRow && Number.isFinite(requestId)) {
        const acceptedAt =
            payment?.requestAcceptedAt ??
            requestAcceptedEventTimes[requestId] ??
            requestAcceptedEventTimes[String(requestId)];
        if (acceptedAt != null && acceptedAt !== '') {
            const acceptedMs = Number(acceptedAt);
            if (Number.isFinite(acceptedMs) && acceptedMs > 0) {
                return acceptedMs;
            }
        }
    }

    const id = Number(payment?.id ?? payment?.paymentId);
    if (Number.isFinite(id) && eventTimes[id]) {
        return Number(eventTimes[id]);
    }

    const paymentTime = payment?.paymentTime;
    if (paymentTime != null && paymentTime !== '') {
        const n = Number(paymentTime);
        if (Number.isFinite(n) && n > 0) {
            // On-chain block.timestamp is seconds; DB ISO strings are ms via Date.
            return n < 1e12 ? n * 1000 : n;
        }
    }

    if (payment?.createdAtMs != null && payment?.createdAtMs !== '') {
        const createdMs = Number(payment.createdAtMs);
        if (Number.isFinite(createdMs) && createdMs > 0) {
            return createdMs;
        }
    }

    if (payment?.createdAt) {
        const parsed = new Date(payment.createdAt).getTime();
        if (Number.isFinite(parsed)) return parsed;
    }

    return null;
};

/**
 * Sort payment history rows with the newest transaction first.
 * Payment IDs on the deployed gateway are random — use time / chain index instead.
 */
export const sortDirectPaymentsNewestFirst = (
    payments = [],
    eventTimes = {},
    requestAcceptedEventTimes = {},
) =>
    [...payments].sort((a, b) => {
        const aTs = getDirectPaymentTimestamp(
            a,
            eventTimes,
            requestAcceptedEventTimes,
        );
        const bTs = getDirectPaymentTimestamp(
            b,
            eventTimes,
            requestAcceptedEventTimes,
        );

        if (aTs != null && bTs != null && bTs !== aTs) {
            return bTs - aTs;
        }
        if (aTs != null && bTs == null) return -1;
        if (aTs == null && bTs != null) return 1;

        // Contract arrays append in creation order — higher index = newer.
        const aIndex = Number(a?.chainIndex ?? -1);
        const bIndex = Number(b?.chainIndex ?? -1);
        if (bIndex !== aIndex) return bIndex - aIndex;

        const aId = Number(a?.id ?? a?.paymentId ?? 0);
        const bId = Number(b?.id ?? b?.paymentId ?? 0);
        return bId - aId;
    });
